import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarX } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { api } from '../../services/api';

type Props = {
  tenantId: string;
  status: string;
  canUpdate: boolean;
  onSuccess: () => void | Promise<void>;
};

export function ExpireContractButton({ tenantId, status, canUpdate, onSuccess }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canUpdate || status === 'cancelado' || status === 'expired') {
    return null;
  }

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await api.expireTenantContract(tenantId, expirationDate.trim() ? { expirationDate: expirationDate.trim() } : {});
      await onSuccess();
      await queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      setOpen(false);
      setExpirationDate('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo marcar el contrato como vencido');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button type="button" variant="secondary" icon={CalendarX} onClick={() => setOpen(true)}>
        Marcar como vencido
      </Button>
      <Modal isOpen={open} onClose={() => !submitting && setOpen(false)} title="Vencimiento del contrato" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Marca el contrato como <strong>vencido</strong> y libera la unidad. Opcionalmente ajusta la fecha de fin del
            contrato en el registro.
          </p>
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva fecha de fin (opcional)</label>
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <CalendarX className="w-4 h-4" />
              {submitting ? 'Guardando...' : 'Confirmar vencimiento'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
